import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface JourneyHighlightDispatchContextValue {
  setHoveredSegmentIndex: (index: number | null) => void;
  clearHoveredSegmentIndex: () => void;
}

const JourneyHighlightStateContext = createContext<number | null>(null);
const JourneyHighlightDispatchContext = createContext<JourneyHighlightDispatchContextValue | null>(null);

export function JourneyHighlightProvider({ children }: { children: React.ReactNode }) {
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null);

  const clearHoveredSegmentIndex = useCallback(() => {
    setHoveredSegmentIndex(null);
  }, []);

  const dispatchValue = useMemo(
    () => ({
      setHoveredSegmentIndex,
      clearHoveredSegmentIndex,
    }),
    [clearHoveredSegmentIndex]
  );

  return (
    <JourneyHighlightDispatchContext.Provider value={dispatchValue}>
      <JourneyHighlightStateContext.Provider value={hoveredSegmentIndex}>
        {children}
      </JourneyHighlightStateContext.Provider>
    </JourneyHighlightDispatchContext.Provider>
  );
}

export function useHoveredSegmentIndex(): number | null {
  return useContext(JourneyHighlightStateContext);
}

export function useSetHoveredSegmentIndex() {
  const context = useContext(JourneyHighlightDispatchContext);
  if (!context) {
    throw new Error('useSetHoveredSegmentIndex must be used within JourneyHighlightProvider');
  }
  return context.setHoveredSegmentIndex;
}

export function useClearHoveredSegmentIndex() {
  const context = useContext(JourneyHighlightDispatchContext);
  if (!context) {
    throw new Error('useClearHoveredSegmentIndex must be used within JourneyHighlightProvider');
  }
  return context.clearHoveredSegmentIndex;
}
